import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { CalendarCheck, Trash, PencilSimple, MapPin, Plus, Clock, Archive } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_EVENT_FORM = { name: '', event_date: '', event_end_date: '', from_time: '', to_time: '' };

export function EventSettingsTab({ token }) {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({ ...INITIAL_EVENT_FORM });
  const [showArchived, setShowArchived] = useState(false);

  const [showCenterDialog, setShowCenterDialog] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [targetEventId, setTargetEventId] = useState(null);
  const [centerForm, setCenterForm] = useState({ name: '' });

  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/events`);
      setEvents(data);
    } catch { toast.error('Failed to fetch events'); }
    finally { setLoadingEvents(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.name.trim()) { toast.error('Please enter an event name'); return; }
    if (!eventForm.event_date) { toast.error('Please select an event date'); return; }
    try {
      if (editingEvent) {
        await axios.put(`${API_URL}/api/events/${editingEvent.id}`, eventForm);
        toast.success('Event updated');
      } else {
        await axios.post(`${API_URL}/api/events`, eventForm);
        toast.success('Event created');
      }
      setShowEventDialog(false); setEditingEvent(null); setEventForm({ ...INITIAL_EVENT_FORM }); fetchEvents();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save event'); }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event and all its triage centers?')) return;
    try {
      await axios.delete(`${API_URL}/api/events/${eventId}`);
      toast.success('Event deleted'); fetchEvents();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete event'); }
  };

  const openAddCenter = (eventId) => {
    setTargetEventId(eventId); setEditingCenter(null); setCenterForm({ name: '' }); setShowCenterDialog(true);
  };
  const openEditCenter = (eventId, center) => {
    setTargetEventId(eventId); setEditingCenter(center); setCenterForm({ name: center.name }); setShowCenterDialog(true);
  };

  const handleSaveCenter = async (e) => {
    e.preventDefault();
    if (!centerForm.name.trim()) { toast.error('Please enter a name'); return; }
    try {
      if (editingCenter) {
        await axios.put(`${API_URL}/api/events/${targetEventId}/triage-centers/${editingCenter.id}`, { name: centerForm.name });
        toast.success('Triage center updated');
      } else {
        await axios.post(`${API_URL}/api/events/${targetEventId}/triage-centers`, { name: centerForm.name });
        toast.success('Triage center added');
      }
      setShowCenterDialog(false); fetchEvents();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save triage center'); }
  };

  const handleDeleteCenter = async (eventId, centerId) => {
    if (!window.confirm('Remove this triage center?')) return;
    try {
      await axios.delete(`${API_URL}/api/events/${eventId}/triage-centers/${centerId}`);
      toast.success('Triage center removed'); fetchEvents();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatTime12 = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const isEventPast = (ev) => {
    const endDate = ev.event_end_date || ev.event_date;
    if (!endDate) return false;
    const today = new Date().toISOString().split('T')[0];
    return endDate < today;
  };

  const activeEvents = events.filter(ev => !isEventPast(ev));
  const archivedEvents = events.filter(ev => isEventPast(ev));
  const displayEvents = showArchived ? archivedEvents : activeEvents;

  return (
    <>
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Event Settings</CardTitle>
            <CardDescription>{activeEvents.length} active events{archivedEvents.length > 0 && `, ${archivedEvents.length} archived`}</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {archivedEvents.length > 0 && (
              <Button
                variant={showArchived ? "default" : "outline"}
                size="sm"
                className={`rounded-sm text-xs ${showArchived ? 'bg-slate-700 hover:bg-slate-600' : ''}`}
                onClick={() => setShowArchived(!showArchived)}
                data-testid="toggle-archived-events"
              >
                <Archive size={14} className="mr-1.5" />
                {showArchived ? 'Show Active' : `Archive (${archivedEvents.length})`}
              </Button>
            )}
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" onClick={() => { setEditingEvent(null); setEventForm({ ...INITIAL_EVENT_FORM }); setShowEventDialog(true); }} data-testid="add-event-button">
              <CalendarCheck size={18} className="mr-2" /> Add Event
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingEvents ? (
            <p className="text-center py-8 text-slate-500">Loading events...</p>
          ) : displayEvents.length === 0 ? (
            <div className="text-center py-12">
              <CalendarCheck size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{showArchived ? 'No archived events.' : 'No active events configured yet.'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {displayEvents.map((ev) => (
                <div key={ev.id} className="border border-slate-200 rounded-sm" data-testid={`event-settings-${ev.id}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div>
                      <h3 className="font-semibold text-slate-900">{ev.name}</h3>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {ev.event_date && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <CalendarCheck size={12} /> {formatDate(ev.event_date)}{ev.event_end_date && ev.event_end_date !== ev.event_date ? ` — ${formatDate(ev.event_end_date)}` : ''}
                          </span>
                        )}
                        {(ev.from_time || ev.to_time) && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock size={12} /> {formatTime12(ev.from_time)} — {formatTime12(ev.to_time)}
                          </span>
                        )}
                        {!ev.event_date && !ev.from_time && (
                          <span className="text-xs text-red-600 italic font-medium">No date set — will not appear on forms. Please edit and add a date.</span>
                        )}
                        {showArchived && (
                          <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-medium">Archived</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingEvent(ev); setEventForm({ name: ev.name, event_date: ev.event_date || '', event_end_date: ev.event_end_date || '', from_time: ev.from_time || '', to_time: ev.to_time || '' }); setShowEventDialog(true); }} data-testid={`edit-event-${ev.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteEvent(ev.id)} data-testid={`delete-event-${ev.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>

                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-slate-500" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Triage Centers ({(ev.triage_centers || []).length})</span>
                      </div>
                      <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" onClick={() => openAddCenter(ev.id)} data-testid={`add-center-${ev.id}`}>
                        <Plus size={14} className="mr-1" /> Add Center
                      </Button>
                    </div>
                    {(!ev.triage_centers || ev.triage_centers.length === 0) ? (
                      <p className="text-sm text-slate-400 italic">No triage centers assigned.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {ev.triage_centers.map((center) => (
                          <div key={center.id} className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-sm px-3 py-1.5" data-testid={`center-${center.id}`}>
                            <MapPin size={14} className="text-blue-600" />
                            <span className="text-sm font-medium text-blue-800">{center.name}</span>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 ml-1 text-blue-600 hover:text-blue-800" onClick={() => openEditCenter(ev.id, center)}>
                              <PencilSimple size={12} />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteCenter(ev.id, center.id)} data-testid={`delete-center-${center.id}`}>
                              <Trash size={12} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Add New Event'}</DialogTitle>
            <DialogDescription>{editingEvent ? 'Update event details' : 'Create a new event with date, time, and triage centers'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEvent} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Event Name *</Label>
              <Input value={eventForm.name} onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })} placeholder="e.g., Concert at Madison Square Garden" required className="rounded-sm" data-testid="event-name-form-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Start Date *</Label>
                <Input type="date" value={eventForm.event_date} onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })} required className="rounded-sm" data-testid="event-date-form-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">End Date</Label>
                <Input type="date" value={eventForm.event_end_date} onChange={(e) => setEventForm({ ...eventForm, event_end_date: e.target.value })} min={eventForm.event_date} className="rounded-sm" data-testid="event-end-date-form-input" />
                <p className="text-[10px] text-slate-400">Leave blank for single-day events</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">From Time</Label>
                <Input type="time" value={eventForm.from_time} onChange={(e) => setEventForm({ ...eventForm, from_time: e.target.value })} className="rounded-sm" data-testid="event-from-time-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">To Time</Label>
                <Input type="time" value={eventForm.to_time} onChange={(e) => setEventForm({ ...eventForm, to_time: e.target.value })} className="rounded-sm" data-testid="event-to-time-input" />
              </div>
            </div>
            <p className="text-xs text-slate-400">Only events matching today's date will appear on the patient form. Set a time range to further restrict visibility (Eastern Time).</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEventDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-event-button">{editingEvent ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Triage Center Dialog */}
      <Dialog open={showCenterDialog} onOpenChange={setShowCenterDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCenter ? 'Edit Triage Center' : 'Add Triage Center'}</DialogTitle>
            <DialogDescription>Name this triage area (e.g., "Medical Tent A", "First Aid North")</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveCenter} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Center Name</Label>
              <Input value={centerForm.name} onChange={(e) => setCenterForm({ name: e.target.value })} placeholder="e.g., Medical Tent A" required className="rounded-sm" data-testid="center-name-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCenterDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-center-button">{editingCenter ? 'Update' : 'Add Center'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
